// src/api/messageApi.js
import { API_config } from '../../config/apiConfig';

const messagesUrl = API_config.messagesApiUrl;
const authHeader = API_config.bearerAuthHeader;

/**
 * Helper to handle fetch with common headers
 */
async function fetchWithAuth(url, options = {}) {
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
        ...options.headers
    };

    const response = await fetch(url, { ...options, headers });
    if (!response.ok) {
        const text = await response.text();
        try {
            const json = JSON.parse(text);
            throw new Error(json.error || json.message || 'API Request Failed');
        } catch (e) {
            throw new Error(`API Request Failed: ${response.statusText} (${text})`);
        }
    }
    return response.json();
}

/**
 * Helper to determine which API to use based on device model
 */
function getMessageEndpointParams(device) {
    if (!device) return { isTwilio: false, baseUrl: API_config.messagesApiUrl };

    // Convert to string to safely compare
    const modelStr = String(device.model || device.attributes?.model || '');
    const isTwilio = modelStr === '1';

    return {
        isTwilio,
        baseUrl: isTwilio ? API_config.twilioMessagesApiUrl : API_config.messagesApiUrl
    };
}

/**
 * Fetches devices specifically for the messaging module
 * Endpoint: GET ?action=devices
 * We will continue to fetch the device list from the primary endpoint,
 * assuming the backend provides the `model` property for all devices.
 */
export async function getMessagingDevices() {
    return fetchWithAuth(`${messagesUrl}?action=devices`);
}

/**
 * Sends a command/message to a device via 1NCE or Twilio
 * Endpoint: POST ?action=send
 * @param {Object} device - The device object containing id, uniqueId/iccid, phone, model
 * @param {string} payload - The message payload
 */
export async function sendMessage(device, payload) {
    const { isTwilio, baseUrl } = getMessageEndpointParams(device);
    const iccid = device.iccid || device.uniqueId;
    const phone = device.phone || device.attributes?.phone || '';
    // Twilio backend looks up by DeviceIDTraccar; 1NCE uses internal id
    const deviceId = isTwilio ? device.DeviceIDTraccar : device.id;

    return fetchWithAuth(`${baseUrl}?action=send`, {
        method: 'POST',
        body: JSON.stringify({ deviceId, iccid, phone, payload })
    });
}

/**
 * Syncs messages from 1NCE or Twilio for a specific device and returns updated history
 * Endpoint: POST ?action=sync
 * @param {Object} device - The device object
 * @param {number} limit - Limit of messages to fetch
 */
export async function syncMessages(device, limit = 50) {
    const { isTwilio, baseUrl } = getMessageEndpointParams(device);
    const iccid = device.iccid || device.uniqueId;
    const phone = device.phone || device.attributes?.phone || '';
    // Twilio backend looks up by DeviceIDTraccar; 1NCE uses internal id
    const deviceId = isTwilio ? device.DeviceIDTraccar : device.id;

    return fetchWithAuth(`${baseUrl}?action=sync`, {
        method: 'POST',
        body: JSON.stringify({ deviceId, iccid, phone, limit })
    });
}

/**
 * Gets message history from local DB (no sync)
 * Endpoint: GET ?action=history&deviceId=...&limit=...
 * @param {Object} params - { device, limit }
 */
export async function getMessages(params) {
    const { device, limit = 50 } = params;
    const { isTwilio, baseUrl } = getMessageEndpointParams(device);
    const phone = device.phone || device.attributes?.phone || '';
    // Twilio backend looks up by DeviceIDTraccar; 1NCE uses internal id
    const deviceId = isTwilio ? device.DeviceIDTraccar : device.id;

    const query = new URLSearchParams({
        action: 'history',
        deviceId,
        limit,
        ...(phone && { phone })
    }).toString();

    return fetchWithAuth(`${baseUrl}?${query}`);
}
